(function () {
  'use strict';

  var ACTIVE_CLASS = 'is-active';
  var FILTERED_CLASS = 'is-filtered';
  var BODY_LOCK_CLASS = 'mc-product-gallery--lightbox-open';

  function toArray(collection) {
    return Array.isArray(collection) ? collection : Array.from(collection || []);
  }

  function normalizeId(value) {
    return value == null ? null : String(value);
  }

  function parseOptions(json) {
    try {
      var parsed = JSON.parse(json || '[]');
      return Array.isArray(parsed) ? parsed.map(function (option) {
        return typeof option === 'string' ? option.toLowerCase() : option;
      }) : [];
    } catch (error) {
      return [];
    }
  }

  function drainInitQueue() {
    if (!Array.isArray(window.mcProductGalleryInitQueue) || window.mcProductGalleryInitQueue.length === 0) {
      return;
    }

    window.mcProductGalleryInitQueue = window.mcProductGalleryInitQueue.filter(function (initializer) {
      if (typeof initializer !== 'function') {
        return false;
      }

      try {
        return initializer() === false;
      } catch (error) {
        console.error('[MC Product Gallery] init failed', error);
        return false;
      }
    });
  }

  var McProductGallery = /*#__PURE__*/function () {
    function McProductGallery(root) {
      this.root = root;
      this.lightboxEnabled = String(root.dataset.lightboxEnabled) === 'true';
      this.productOptions = parseOptions(root.dataset.productOptions);
      this.section = root.closest('[data-section-id]');
      this.mainSlides = toArray(root.querySelectorAll('[data-gallery-slide]'));
      this.thumbnails = toArray(root.querySelectorAll('[data-gallery-thumbnail]'));
      this.lightbox = this.lightboxEnabled ? root.querySelector('[data-gallery-lightbox]') : null;
      this.lightboxSlides = this.lightbox ? toArray(this.lightbox.querySelectorAll('[data-gallery-lightbox-slide]')) : [];
      this.lightboxThumbnails = this.lightbox ? toArray(this.lightbox.querySelectorAll('[data-gallery-lightbox-thumbnail]')) : [];
      this._listeners = [];
      this._isLightboxOpen = false;
      this._onEscapeHandler = null;
      this._previouslyFocused = null;
      this._lightboxReturnParent = null;
      this._lightboxPlaceholder = null;
      this.entries = this._buildEntries();
      this.activeKey = this._findInitialActiveKey();
      this._applyInitialState();
      this._bindEvents();
    }

    var _proto = McProductGallery.prototype;

    _proto._buildEntries = function _buildEntries() {
      var _this = this;
      var entries = new Map();
      this.mainSlides.forEach(function (slide) {
        var key = normalizeId(slide.dataset.mediaKey);
        if (!key || entries.has(key)) {
          return;
        }
        var entry = {
          key: key,
          slide: slide,
          thumbnail: _this.thumbnails.find(function (thumb) {
            return normalizeId(thumb.dataset.mediaKey) === key;
          }) || null,
          lightboxSlide: _this.lightboxSlides.find(function (item) {
            return normalizeId(item.dataset.mediaKey) === key;
          }) || null,
          lightboxThumbnail: _this.lightboxThumbnails.find(function (thumb) {
            return normalizeId(thumb.dataset.mediaKey) === key;
          }) || null,
          groupName: slide.dataset.groupName || null,
          groupValue: slide.dataset.groupValue || null,
          variantIds: (slide.dataset.variantIds || '').split('|').map(function (value) {
            return value ? value.trim() : '';
          }).filter(Boolean),
          mediaType: slide.dataset.mediaType || 'image',
          isPromo: key.indexOf('promo-') === 0,
          isHidden: slide.classList.contains(FILTERED_CLASS)
        };
        entries.set(key, entry);
      });
      return entries;
    };

    _proto._findInitialActiveKey = function _findInitialActiveKey() {
      var active = this.mainSlides.find(function (slide) {
        return slide.classList.contains(ACTIVE_CLASS);
      });
      if (active) {
        return normalizeId(active.dataset.mediaKey);
      }
      var firstVisible = this.mainSlides.find(function (slide) {
        return !slide.classList.contains(FILTERED_CLASS);
      });
      if (firstVisible) {
        return normalizeId(firstVisible.dataset.mediaKey);
      }
      return this.mainSlides.length ? normalizeId(this.mainSlides[0].dataset.mediaKey) : null;
    };

    _proto._applyInitialState = function _applyInitialState() {
      var _this2 = this;
      var hasVisible = false;
      this.entries.forEach(function (entry) {
        _this2._toggleFiltered(entry, entry.isHidden);
        if (!entry.isHidden && !hasVisible) {
          hasVisible = true;
        }
      });
      if (!hasVisible) {
        this.entries.forEach(function (entry) {
          _this2._toggleFiltered(entry, false);
        });
        hasVisible = this.entries.size > 0;
      }
      if (!this.activeKey || !this.entries.has(this.activeKey) || this.entries.get(this.activeKey).isHidden) {
        var firstAvailable = this._firstVisibleEntry();
        this.activeKey = firstAvailable ? firstAvailable.key : null;
      }
      if (this.activeKey) {
        this.setActive(this.activeKey, { suppressScroll: true, force: true });
      }
    };

    _proto._bindEvents = function _bindEvents() {
      var _this3 = this;
      this.thumbnails.forEach(function (thumbnail) {
        var handler = function handler(event) {
          event.preventDefault();
          if (thumbnail.classList.contains(FILTERED_CLASS) || thumbnail.getAttribute('aria-hidden') === 'true') {
            return;
          }
          _this3.setActive(thumbnail.dataset.mediaKey);
        };
        thumbnail.addEventListener('click', handler);
        _this3._listeners.push({ element: thumbnail, type: 'click', handler: handler });
      });

      if (this.lightboxEnabled) {
        this.mainSlides.forEach(function (slide) {
          var clickHandler = function clickHandler(event) {
            if (_this3._shouldIgnoreMediaClick(event)) {
              return;
            }
            event.preventDefault();
            _this3.openLightbox();
          };
          var keyHandler = function keyHandler(event) {
            if (event.key === 'Enter' || event.key === ' ') {
              if (_this3._shouldIgnoreMediaClick(event)) {
                return;
              }
              event.preventDefault();
              _this3.openLightbox();
            }
          };
          slide.addEventListener('click', clickHandler);
          slide.addEventListener('keydown', keyHandler);
          _this3._listeners.push({ element: slide, type: 'click', handler: clickHandler });
          _this3._listeners.push({ element: slide, type: 'keydown', handler: keyHandler });
        });

        toArray(this.root.querySelectorAll('[data-gallery-open]')).forEach(function (trigger) {
          var handler = function handler(event) {
            event.preventDefault();
            _this3.openLightbox();
          };
          trigger.addEventListener('click', handler);
          _this3._listeners.push({ element: trigger, type: 'click', handler: handler });
        });

        if (this.lightbox) {
          var lightboxClickHandler = function lightboxClickHandler(event) {
            var closeTrigger = event.target.closest('[data-gallery-close]');
            if (!closeTrigger) {
              return;
            }
            if (closeTrigger.classList.contains('mc-product-gallery__lightbox-backdrop') && event.target !== closeTrigger) {
              return;
            }
            event.preventDefault();
            _this3.closeLightbox();
          };
          this.lightbox.addEventListener('click', lightboxClickHandler);
          this._listeners.push({ element: this.lightbox, type: 'click', handler: lightboxClickHandler });

          this.lightboxThumbnails.forEach(function (thumbnail) {
            var handler = function handler(event) {
              event.preventDefault();
              _this3.setActive(thumbnail.dataset.mediaKey, { origin: 'lightbox' });
            };
            thumbnail.addEventListener('click', handler);
            _this3._listeners.push({ element: thumbnail, type: 'click', handler: handler });
          });
        }
      }

      if (this.section) {
        this._onVariantChange = this._handleVariantChange.bind(this);
        this.section.addEventListener('variant:change', this._onVariantChange);
        this._listeners.push({ element: this.section, type: 'variant:change', handler: this._onVariantChange });
      }
    };

    _proto._shouldIgnoreMediaClick = function _shouldIgnoreMediaClick(event) {
      var target = event.target;
      if (!target) {
        return false;
      }
      if (target.closest('[data-gallery-thumbnail]')) {
        return true;
      }
      var tagName = target.tagName ? target.tagName.toLowerCase() : '';
      if (tagName === 'button' || tagName === 'a' || tagName === 'video' || tagName === 'model-viewer' || tagName === 'iframe') {
        return true;
      }
      if (target.closest('[data-gallery-dismiss]')) {
        return true;
      }
      return false;
    };

    _proto.openLightbox = function openLightbox() {
      var _this4 = this;
      if (!this.lightboxEnabled || !this.lightbox || this._isLightboxOpen) {
        return;
      }
      if (this.lightbox.parentElement !== document.body) {
        this._lightboxReturnParent = this.lightbox.parentElement;
        if (!this._lightboxPlaceholder) {
          this._lightboxPlaceholder = document.createElement('div');
          this._lightboxPlaceholder.className = 'mc-product-gallery__lightbox-placeholder';
        }
        this._lightboxReturnParent.insertBefore(this._lightboxPlaceholder, this.lightbox.nextSibling);
        document.body.appendChild(this.lightbox);
      }
      this._isLightboxOpen = true;
      this._previouslyFocused = document.activeElement;
      this.lightbox.classList.add('is-open');
      this.lightbox.removeAttribute('aria-hidden');
      document.body.classList.add(BODY_LOCK_CLASS);
      var closeButton = this.lightbox.querySelector('.mc-product-gallery__lightbox-close');
      if (closeButton) {
        closeButton.focus({ preventScroll: true });
      }
      this.setActive(this.activeKey, { origin: 'lightbox', suppressScroll: true, force: true });
      this._onEscapeHandler = function (event) {
        if (event.key === 'Escape') {
          _this4.closeLightbox();
        }
      };
      document.addEventListener('keydown', this._onEscapeHandler);
    };

    _proto.closeLightbox = function closeLightbox() {
      if (!this._isLightboxOpen || !this.lightbox) {
        return;
      }
      this._isLightboxOpen = false;
      this.lightbox.classList.remove('is-open');
      this.lightbox.setAttribute('aria-hidden', 'true');
      document.body.classList.remove(BODY_LOCK_CLASS);
      if (this._onEscapeHandler) {
        document.removeEventListener('keydown', this._onEscapeHandler);
        this._onEscapeHandler = null;
      }
      if (this._previouslyFocused && typeof this._previouslyFocused.focus === 'function' && document.body.contains(this._previouslyFocused)) {
        this._previouslyFocused.focus({ preventScroll: true });
      }
      this._previouslyFocused = null;
      if (this._lightboxReturnParent) {
        if (this._lightboxPlaceholder && this._lightboxPlaceholder.parentElement === this._lightboxReturnParent) {
          this._lightboxReturnParent.insertBefore(this.lightbox, this._lightboxPlaceholder);
          this._lightboxPlaceholder.remove();
        } else {
          this._lightboxReturnParent.appendChild(this.lightbox);
        }
        this._lightboxReturnParent = null;
      }
    };

    _proto.setActive = function setActive(key, options) {
      var _this5 = this;
      if (options === void 0) {
        options = {};
      }
      var resolvedKey = normalizeId(key);
      if (!resolvedKey || !this.entries.has(resolvedKey) || this.entries.get(resolvedKey).isHidden) {
        var fallback = this._firstVisibleEntry();
        if (!fallback) {
          return;
        }
        resolvedKey = fallback.key;
      }
      if (this.activeKey === resolvedKey && !options.force) {
        if (options.origin === 'lightbox') {
          var entry = this.entries.get(resolvedKey);
          if (entry && entry.lightboxSlide) {
            entry.lightboxSlide.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          }
        }
        return;
      }
      var previousEntry = this.activeKey ? this.entries.get(this.activeKey) : null;
      if (previousEntry) {
        [previousEntry.slide, previousEntry.thumbnail, previousEntry.lightboxSlide, previousEntry.lightboxThumbnail].forEach(function (element) {
          element && element.classList.remove(ACTIVE_CLASS);
        });
        if (previousEntry.lightboxSlide) {
          previousEntry.lightboxSlide.setAttribute("aria-hidden", "true");
        }
      }
      var nextEntry = this.entries.get(resolvedKey);
      if (!nextEntry) {
        return;
      }
      [nextEntry.slide, nextEntry.thumbnail, nextEntry.lightboxSlide, nextEntry.lightboxThumbnail].forEach(function (element) {
        element && element.classList.add(ACTIVE_CLASS);
      });
      if (nextEntry.lightboxSlide) {
        nextEntry.lightboxSlide.removeAttribute("aria-hidden");
      }
      this.activeKey = resolvedKey;

      if (!options.suppressScroll && nextEntry.thumbnail) {
        window.requestAnimationFrame(function () {
          nextEntry.thumbnail.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        });
      }

      if (options.origin === 'lightbox' && nextEntry.lightboxSlide) {
        window.requestAnimationFrame(function () {
          nextEntry.lightboxSlide.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        });
      }

      if (options.focusSlide && nextEntry.slide) {
        window.requestAnimationFrame(function () {
          nextEntry.slide.focus({ preventScroll: true });
        });
      }

      this.entries.forEach(function (entry) {
        if (entry.slide && entry.slide.dataset.mediaType === 'video') {
          var videos = entry.slide.querySelectorAll('video');
          videos.forEach(function (video) {
            if (entry.key === _this5.activeKey) {
              return;
            }
            try {
              video.pause();
            } catch (error) {
              // No-op
            }
          });
        }
      });
    };

    _proto._firstVisibleEntry = function _firstVisibleEntry() {
      var iterator = this.entries.values();
      var current = iterator.next();
      while (!current.done) {
        if (!current.value.isHidden) {
          return current.value;
        }
        current = iterator.next();
      }
      return null;
    };

    _proto._toggleFiltered = function _toggleFiltered(entry, hidden) {
      entry.isHidden = !!hidden;
      if (entry.slide) {
        entry.slide.classList.toggle(FILTERED_CLASS, hidden);
        if (hidden) {
          entry.slide.setAttribute('aria-hidden', 'true');
          entry.slide.setAttribute('hidden', '');
        } else {
          entry.slide.removeAttribute('aria-hidden');
          entry.slide.removeAttribute('hidden');
        }
        if (this.lightboxEnabled && entry.slide.dataset.mediaType === 'image') {
          if (hidden) {
            entry.slide.setAttribute('tabindex', '-1');
          } else {
            entry.slide.setAttribute('tabindex', '0');
            entry.slide.setAttribute('role', 'button');
            if (!entry.slide.getAttribute('aria-label')) {
              var imageEl = entry.slide.querySelector('img');
              var altText = imageEl ? imageEl.getAttribute('alt') : '';
              if (altText) {
                entry.slide.setAttribute('aria-label', altText);
              }
            }
          }
        } else if (entry.slide.dataset.mediaType !== 'image') {
          entry.slide.removeAttribute('tabindex');
          entry.slide.removeAttribute('role');
        }
      }
      if (entry.thumbnail) {
        entry.thumbnail.classList.toggle(FILTERED_CLASS, hidden);
        if (hidden) {
          entry.thumbnail.setAttribute('aria-hidden', 'true');
          entry.thumbnail.setAttribute('hidden', '');
        } else {
          entry.thumbnail.removeAttribute('aria-hidden');
          entry.thumbnail.removeAttribute('hidden');
        }
      }
      if (entry.lightboxSlide) {
        entry.lightboxSlide.classList.toggle(FILTERED_CLASS, hidden);
        entry.lightboxSlide.setAttribute('aria-hidden', hidden ? 'true' : 'false');
      }
      if (entry.lightboxThumbnail) {
        entry.lightboxThumbnail.classList.toggle(FILTERED_CLASS, hidden);
        if (hidden) {
          entry.lightboxThumbnail.setAttribute('aria-hidden', 'true');
          entry.lightboxThumbnail.setAttribute('hidden', '');
        } else {
          entry.lightboxThumbnail.removeAttribute('aria-hidden');
          entry.lightboxThumbnail.removeAttribute('hidden');
        }
      }
    };

    _proto._handleVariantChange = function _handleVariantChange(event) {
      var _this6 = this;
      if (!event || !event.detail || !event.detail.variant) {
        return;
      }
      var variant = event.detail.variant;
      var featuredMediaId = normalizeId(variant.featured_media && variant.featured_media.id);
      var normalizedOptions = this._normalizeVariantOptions(variant);
      var firstVisibleKey = null;
      var nextActiveKey = null;
      this.entries.forEach(function (entry) {
        var shouldDisplay = _this6._entryMatchesVariant(entry, variant, normalizedOptions, featuredMediaId);
        _this6._toggleFiltered(entry, !shouldDisplay);
        if (shouldDisplay) {
          if (!firstVisibleKey) {
            firstVisibleKey = entry.key;
          }
          if (featuredMediaId && entry.key === featuredMediaId) {
            nextActiveKey = entry.key;
          }
        } else if (entry.key === _this6.activeKey) {
          nextActiveKey = null;
        }
      });
      if (nextActiveKey) {
        this.setActive(nextActiveKey, { suppressScroll: true, focusSlide: true });
      } else if (!this.activeKey || !this.entries.has(this.activeKey) || this.entries.get(this.activeKey).isHidden) {
        if (firstVisibleKey) {
          this.setActive(firstVisibleKey, { suppressScroll: true, focusSlide: true });
        }
      }
    };

    _proto._normalizeVariantOptions = function _normalizeVariantOptions(variant) {
      var optionsMap = new Map();
      var _this$productOptions = this.productOptions,
        productOptions = _this$productOptions === void 0 ? [] : _this$productOptions;
      productOptions.forEach(function (optionName, index) {
        var key = typeof optionName === 'string' ? optionName.toLowerCase() : '';
        if (!key) {
          return;
        }
        var variantValue = variant['option' + (index + 1)];
        optionsMap.set(key, typeof variantValue === 'string' ? variantValue.toLowerCase() : '');
      });
      return optionsMap;
    };

    _proto._entryMatchesVariant = function _entryMatchesVariant(entry, variant, optionsMap, featuredMediaId) {
      if (entry.isPromo) {
        return true;
      }
      if (featuredMediaId && entry.key === featuredMediaId) {
        return true;
      }
      if (entry.variantIds.length && entry.variantIds.includes(String(variant.id))) {
        return true;
      }
      if (entry.groupName && entry.groupValue) {
        var optionValue = optionsMap.get(entry.groupName);
        if (optionValue && optionValue === entry.groupValue) {
          return true;
        }
        return false;
      }
      return true;
    };

    _proto.destroy = function destroy() {
      if (this._isLightboxOpen) {
        this.closeLightbox();
      } else if (this.lightbox && this.lightbox.parentElement === document.body && this._lightboxReturnParent) {
        if (this._lightboxPlaceholder && this._lightboxPlaceholder.parentElement === this._lightboxReturnParent) {
          this._lightboxReturnParent.insertBefore(this.lightbox, this._lightboxPlaceholder);
          this._lightboxPlaceholder.remove();
        } else {
          this._lightboxReturnParent.appendChild(this.lightbox);
        }
        this._lightboxReturnParent = null;
      }
      if (this._lightboxPlaceholder && this._lightboxPlaceholder.parentElement) {
        this._lightboxPlaceholder.remove();
      }
      this._lightboxPlaceholder = null;
      if (this._onEscapeHandler) {
        document.removeEventListener('keydown', this._onEscapeHandler);
      }
      this._listeners.forEach(function (_ref) {
        var element = _ref.element,
          type = _ref.type,
          handler = _ref.handler;
        element && element.removeEventListener(type, handler);
      });
      this._listeners = [];
      this.entries.clear();
      this.root.removeAttribute('data-mc-gallery-initialized');
    };

    return McProductGallery;
  }();

  var instances = new WeakMap();

  function getElement(elementOrSelector) {
    if (!elementOrSelector) {
      return null;
    }
    if (typeof elementOrSelector === 'string') {
      return document.querySelector(elementOrSelector);
    }
    return elementOrSelector;
  }

  var manager = {
    init: function init(elementOrSelector) {
      var element = getElement(elementOrSelector);
      if (!element || element.dataset.mcGalleryInitialized === 'true') {
        return instances.get(element) || null;
      }
      var gallery = new McProductGallery(element);
      element.dataset.mcGalleryInitialized = 'true';
      instances.set(element, gallery);
      return gallery;
    },
    initAllIn: function initAllIn(container) {
      var scope = container || document;
      toArray(scope.querySelectorAll('[data-mc-product-gallery]')).forEach(this.init, this);
    },
    destroy: function destroy(elementOrSelector) {
      var element = getElement(elementOrSelector);
      if (!element) {
        return;
      }
      var instance = instances.get(element);
      if (instance && typeof instance.destroy === 'function') {
        instance.destroy();
      }
      instances.delete(element);
      element.removeAttribute('data-mc-gallery-initialized');
    }
  };

  window.McProductGalleryManager = manager;

  document.addEventListener('DOMContentLoaded', function () {
    manager.initAllIn(document);
    drainInitQueue();
  });

  document.addEventListener('shopify:section:load', function (event) {
    manager.initAllIn(event.target);
    drainInitQueue();
  });

  document.addEventListener('shopify:section:unload', function (event) {
    toArray(event.target.querySelectorAll('[data-mc-product-gallery]')).forEach(function (element) {
      manager.destroy(element);
    });
  });

  drainInitQueue();
  manager.initAllIn(document);
})();
